import { Fragment, useState } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { cn } from '#app/utils/misc.tsx'
import {
	formatWhatsNewDate,
	parseWhatsNewBody,
	useWhatsNew,
	type WhatsNewInlineToken,
	type WhatsNewInternalItem,
	type WhatsNewItemWithRead,
} from '#app/utils/whats-new.ts'

export function UserNotifications() {
	const { items, unreadCount, markAsRead, markAllAsRead } = useWhatsNew()
	const [popoverOpen, setPopoverOpen] = useState(false)
	const [selectedItem, setSelectedItem] = useState<WhatsNewInternalItem | null>(
		null,
	)

	function openItem(item: WhatsNewItemWithRead) {
		markAsRead(item.id)
		setPopoverOpen(false)
		if (item.kind === 'internal') setSelectedItem(item)
	}

	return (
		<>
			<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="relative inline-flex items-center justify-center rounded-md p-2 text-navbar-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-navbar"
						aria-label={
							unreadCount > 0
								? `Novedades, ${unreadCount} sin leer`
								: 'Novedades'
						}
						title="Novedades"
					>
						<Icon name="bell-solid" size="md" />
						{unreadCount > 0 ? (
							<span
								className="absolute right-1.5 top-1.5 size-2 rounded-full bg-primary"
								aria-hidden="true"
							/>
						) : null}
					</button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					sideOffset={8}
					className="w-80 p-0 sm:w-96"
					onOpenAutoFocus={(event) => event.preventDefault()}
				>
					<div className="flex items-center justify-between gap-2 border-b px-3 py-2">
						<p className="text-body-sm font-semibold">Novedades</p>
						<button
							type="button"
							className="text-body-xs font-semibold text-accent hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40"
							disabled={unreadCount === 0}
							onClick={markAllAsRead}
						>
							Marcar todo como leído
						</button>
					</div>
					{items.length === 0 ? (
						<p className="px-3 py-6 text-center text-body-sm text-secondary-90">
							No hay novedades por ahora.
						</p>
					) : (
						<ul className="max-h-80 overflow-y-auto py-1">
							{items.map((item) => (
								<li key={item.id} className="flex items-start gap-0.5 px-1">
									{item.isRead ? (
										<span className="mt-3 w-7 shrink-0" aria-hidden="true" />
									) : (
										<button
											type="button"
											className="mt-2 flex size-7 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
											aria-label={`Marcar "${item.title}" como leído`}
											title="Marcar como leído"
											onClick={() => markAsRead(item.id)}
										>
											<span className="size-2 rounded-full bg-primary" />
										</button>
									)}
									{item.kind === 'external' ? (
										<a
											href={item.url}
											target="_blank"
											rel="noopener noreferrer"
											className="min-w-0 flex-1 rounded-md px-2 py-2 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
											onClick={() => openItem(item)}
										>
											<WhatsNewPreview item={item} />
										</a>
									) : (
										<button
											type="button"
											className="min-w-0 flex-1 rounded-md px-2 py-2 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
											onClick={() => openItem(item)}
										>
											<WhatsNewPreview item={item} />
										</button>
									)}
								</li>
							))}
						</ul>
					)}
				</PopoverContent>
			</Popover>

			<Dialog
				open={selectedItem !== null}
				onOpenChange={(open) => {
					if (!open) setSelectedItem(null)
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{selectedItem?.title}</DialogTitle>
						{selectedItem ? (
							<time
								className="text-body-xs text-secondary-50"
								dateTime={selectedItem.publishedAt}
							>
								{formatWhatsNewDate(selectedItem.publishedAt)}
							</time>
						) : null}
					</DialogHeader>
					{selectedItem ? (
						<>
							<DialogDescription className="sr-only">
								{selectedItem.body}
							</DialogDescription>
							<div className="space-y-3 text-body-sm text-secondary-90">
								{parseWhatsNewBody(selectedItem.body).map(
									(paragraph, index) => (
										<p key={index} className="whitespace-pre-wrap">
											{paragraph.map(renderWhatsNewToken)}
										</p>
									),
								)}
							</div>
						</>
					) : null}
					<DialogFooter>
						<Button type="button" onClick={() => setSelectedItem(null)}>
							Cerrar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}

function renderWhatsNewToken(token: WhatsNewInlineToken, index: number) {
	if (token.type === 'bold') {
		return (
			<strong key={index} className="font-semibold text-foreground">
				{token.value}
			</strong>
		)
	}
	if (token.type === 'italic') {
		return <em key={index}>{token.value}</em>
	}
	return <Fragment key={index}>{token.value}</Fragment>
}

function WhatsNewPreview({ item }: { item: WhatsNewItemWithRead }) {
	return (
		<>
			<span className="flex items-start justify-between gap-2">
				<span
					className={cn(
						'text-body-sm',
						item.isRead ? 'font-medium text-secondary-90' : 'font-semibold',
					)}
				>
					{item.title}
				</span>
				{item.kind === 'external' ? (
					<Icon
						name="link-2"
						size="sm"
						className="mt-0.5 shrink-0 text-secondary-90"
						title="Se abre en una pestaña nueva"
					/>
				) : null}
			</span>
			<span className="mt-0.5 block text-body-xs text-secondary-90">
				{item.preview}
			</span>
			<time
				className="mt-1 block text-body-xs text-secondary-50"
				dateTime={item.publishedAt}
			>
				{formatWhatsNewDate(item.publishedAt)}
			</time>
		</>
	)
}
